// Bridges Auth0's hook-based token retrieval into non-React code (the RTK Query base
// query). `getAccessTokenSilently` can only be obtained from the `useAuth0()` hook, but
// `prepareHeaders` runs outside React. A small component near the app root registers the
// function here once Auth0 is ready (see `app.tsx`), and the base query reads it per request.

type AccessTokenGetter = () => Promise<string>;
type SessionExpiredHandler = () => void;

let accessTokenGetter: AccessTokenGetter | null = null;
let sessionExpiredHandler: SessionExpiredHandler | null = null;
// Fire the handler at most once per registration — a page's worth of parallel queries all
// fail the same way, and the handler redirects to Auth0, so one invocation is enough.
let sessionExpiredNotified = false;

// Auth0 error codes meaning the cached session can no longer mint access tokens and only a
// fresh interactive login will fix it. Anything else (network blip, offline) is transient,
// and forcing a login redirect on it would kick users out over a dropped connection.
const REAUTH_REQUIRED_ERRORS = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
]);

export const setAccessTokenGetter = (getter: AccessTokenGetter | null): void => {
  accessTokenGetter = getter;
};

export const setSessionExpiredHandler = (handler: SessionExpiredHandler | null): void => {
  sessionExpiredHandler = handler;
  sessionExpiredNotified = false;
};

// Returns a fresh Auth0 access token, or null if Auth0 isn't ready yet or the user has no
// valid session. Callers attach it as a Bearer token; a null result simply means the
// request goes out unauthenticated (the API will answer 401).
//
// When the failure is an expired/revoked session (not a transient error), the registered
// session-expired handler is invoked so the app can force a re-login. Without this the
// Auth0 localStorage cache keeps `isAuthenticated` true, the guarded routes render, every
// API call 401s, and the user is stuck on a loaded-but-empty app.
export const getAccessToken = async (): Promise<string | null> => {
  if (!accessTokenGetter) {
    return null;
  }
  try {
    return await accessTokenGetter();
  } catch (error) {
    const code = (error as { error?: unknown } | null)?.error;
    if (
      typeof code === "string" &&
      REAUTH_REQUIRED_ERRORS.has(code) &&
      sessionExpiredHandler &&
      !sessionExpiredNotified
    ) {
      sessionExpiredNotified = true;
      sessionExpiredHandler();
    }
    return null;
  }
};
